import type { EChartsOption } from "echarts";
import {
  ISprintChartData,
  sprintChartToBarData,
  AdaptedQuality,
} from "apps/ai-metrics/utils/dashboardAdapter";
import {
  createGroupedBarOption,
  createStackedBarOption,
  createGroupedStackedBarOption,
  comparisonColorAt,
  COMPARISON_COLORS,
  formatMonthLabels,
} from "apps/ai-metrics/utils/echartOptions";
import { SectionChart } from "apps/ai-metrics/components/shared/SectionChart/SectionChart";
import { SectionHeader } from "apps/ai-metrics/components/shared/SectionHeader/SectionHeader";
import { StatCard } from "apps/ai-metrics/components/shared/StatCard/StatCard";
import { useSectionStyles } from "apps/ai-metrics/components/shared/useSectionStyles";
import {
  SECTION_NOTES,
  getMetricHelp,
  getChartHelp,
} from "apps/ai-metrics/config/metricHelpText";
import { useStyles as useChartsRowStyles } from "./QualitySection.styles";

const CARD_META = [
  {
    label: "REWORK RATIO",
    subtitle: "Items returned to Active",
    key: "reworkRatio" as const,
    higherIsBetter: false,
  },
  {
    label: "FAILED SCENARIOS / ITEM",
    subtitle: "Avg failed test scenarios per item",
    key: "failedScenarios" as const,
    higherIsBetter: false,
  },
];

function buildTestCasesChartOption(
  data: ISprintChartData,
  comparisonEnabled: boolean,
  teamCount: number,
): EChartsOption {
  const multiTeam = teamCount > 1;

  if (!comparisonEnabled) {
    if (multiTeam) {
      return createStackedBarOption({
        xLabels: formatMonthLabels(data.labels),
        series: data.teams.map((t) => ({
          name: t.team,
          data: t.current,
          color: t.color,
        })),
        yAxisLabel: "Test Cases / Item",
      });
    }
    const bar = sprintChartToBarData(data, false);
    return createGroupedBarOption({
      xLabels: formatMonthLabels(bar.labels),
      series: bar.series.map((s, i) => ({
        name: s.name,
        data: s.values,
        color: comparisonColorAt(i, bar.series.length),
        pointLabels: s.pointLabels,
      })),
      yAxisLabel: "Test Cases / Item",
    });
  }

  if (multiTeam) {
    return createGroupedStackedBarOption({
      xLabels: data.labels,
      teams: data.teams,
      currentLabel: data.currentLabel,
      priorLabel: data.priorLabel,
      yAxisLabel: "Test Cases / Item",
    });
  }
  const bar = sprintChartToBarData(data, true);
  return createGroupedBarOption({
    xLabels: bar.labels,
    series: bar.series.map((s) => ({ name: s.name, data: s.values })),
    yAxisLabel: "Test Cases / Item",
  });
}

function buildTestCasesLegend(
  data: ISprintChartData,
  comparisonEnabled: boolean,
  teamCount: number,
): { name: string; color: string }[] {
  const multiTeam = teamCount > 1;

  if (!comparisonEnabled) {
    if (multiTeam) {
      return data.teams.map((t) => ({ name: t.team, color: t.color }));
    }
    return [{ name: data.currentLabel, color: COMPARISON_COLORS.current }];
  }

  if (multiTeam) {
    return data.teams.map((t) => ({ name: t.team, color: t.color }));
  }
  return [
    { name: data.priorLabel, color: COMPARISON_COLORS.prior },
    { name: data.currentLabel, color: COMPARISON_COLORS.current },
  ];
}

interface IQualitySectionProps {
  kpis?: AdaptedQuality;
  loading?: boolean;
  testCasesByTeam?: ISprintChartData;
  testCasesLoading?: boolean;
  testCasesError?: boolean;
  comparisonEnabled: boolean;
  teamCount: number;
}

export const QualitySection = ({
  kpis,
  loading,
  testCasesByTeam,
  testCasesLoading,
  testCasesError,
  comparisonEnabled,
  teamCount,
}: IQualitySectionProps): JSX.Element => {
  const styles = useSectionStyles();
  const chartsRowStyles = useChartsRowStyles();

  const testCasesChart = testCasesByTeam
    ? buildTestCasesChartOption(testCasesByTeam, comparisonEnabled, teamCount)
    : undefined;

  const testCasesLegend = testCasesByTeam
    ? buildTestCasesLegend(testCasesByTeam, comparisonEnabled, teamCount)
    : undefined;

  return (
    <div className={styles.section}>
      <SectionHeader title="Quality - Team View" note={SECTION_NOTES.quality}>
        <div className={styles.cardsRow}>
          {CARD_META.map(({ label, subtitle, key, higherIsBetter }) => (
            <StatCard
              key={label}
              label={label}
              subtitle={subtitle}
              higherIsBetter={higherIsBetter}
              loading={loading}
              {...(!loading ? (kpis?.[key] ?? { pending: true }) : {})}
              info={getMetricHelp(label)}
            />
          ))}
        </div>
        <div className={chartsRowStyles.chartsRow}>
          <SectionChart
            title="Avg Test Cases / Item by Team"
            option={
              testCasesChart
                ? { ...testCasesChart, legend: { show: false } }
                : undefined
            }
            loading={testCasesLoading}
            emptyMessage={
              !testCasesLoading && !testCasesByTeam
                ? testCasesError
                  ? "Failed to load data"
                  : "No data for selected period"
                : undefined
            }
            legend={testCasesLegend}
            info={getChartHelp("Avg Test Cases / Item by Team")}
          />
        </div>
      </SectionHeader>
    </div>
  );
};
